import React from 'react';
import { useEffect, useState } from 'react';
import { config } from '@abyss/web/tools/config';
import { datadogLogs } from '@datadog/browser-logs';
import packageJson from '../../../../../package.json';
import { datadogRum } from '@datadog/browser-rum';
import { getCloudData } from '../../lagoon/lagoonUtil';
import { App } from '../App/App';
import { LDProvider } from '@/common/LDProvider';

const defaultDomainExclusions = [
  'lpsnmedia.net(\\/[^]*)?$',
  'iperceptions.com(\\/[^]*)?$',
  'azureedge.net(\\/[^]*)?$',
  'adobedtm.com(\\/[^]*)?$',
  'async-msg[^.]*\\.optum\\.com(\\/[^]*)?$',
  'cloudfront.net(\\/[^]*)?$',
  'doubleclick.net(\\/[^]*)?$',
  'liveperson.net(\\/[^]*)?$',
  'optumtrax\\.optum\\.com(\\/[^]*)?$',
  'qualtrics.com(\\/[^]*)?$',
  'smetrics\\.uhc\\.com(\\/[^]*)?$',
  'omtrdc.net(\\/[^]*)?$',
  'ometrics\\.optum\\.com(\\/[^]*)?$',
  'sessioncam.com(\\/[^]*)?$',
  'facebook.com(\\/[^]*)?$',
  'google.com(\\/[^]*)?$',
  'cloudflare.com(\\/[^]*)?$',
  'abyss-cloud\\.uhc\\.com(\\/[^]*)?$',
  'nextportal-cdn.prod.werally.in(\\/[^]*)?$',
  'mapbox.com(\\/[^]*)?$',
];

export const DatadogAppWrapper = () => {
  const envVar = config('ENV_VAR');

  const [isDatadogReady, setIsDatadogReady] = useState<boolean>(false);
  const [shouldSkipDatadog, setShouldSkipDatadog] = useState<boolean>(false);
  const lagoonEnv = config('LAGOON_ENV') ?? 'dev';

  const excludedActivityPattern = new RegExp(
    `^https:\/\/((www|beta)\\.)?(${defaultDomainExclusions.join('|')})`
  );

  useEffect(() => {
    const initializeDatadog = async () => {
      try {
        const cloudData = await getCloudData('sso-container', lagoonEnv, 'en');
        const configs = cloudData['config'] || {};
        const applicationId = configs['DD_APPLICATION_ID']?.value || '';
        const clientToken = configs['DD_CLIENT_TOKEN']?.value || '';
        const service = configs['DD_SERVICE_NAME']?.value || '';
        const dataDogReplaceFallbackTime =
          configs['DD_READY_FALLBACK_TIMEOUT_MILLISECS']?.value;

        if (!applicationId || !clientToken || !service) {
          setShouldSkipDatadog(true);
          return;
        }

        datadogLogs.init({
          clientToken,
          site: 'datadoghq.com',
          service,
          forwardErrorsToLogs: true,
          sessionSampleRate: 100,
          env: envVar,
          version: packageJson.version,
        });

        datadogRum.init({
          applicationId,
          clientToken,
          site: 'datadoghq.com',
          service,
          env: envVar,
          version: packageJson.version,
          sessionSampleRate: 100,
          sessionReplaySampleRate: 100,
          trackUserInteractions: true,
          trackResources: true,
          trackLongTasks: true,
          traceSampleRate: 100,
          allowedTracingUrls: [
            `${window.location.protocol}//${window.location.host}`,
          ],
          excludedActivityUrls: [excludedActivityPattern],
          storeContextsAcrossPages: true,
          defaultPrivacyLevel: 'mask-user-input',
        });

        const fallbackTimeout = setTimeout(() => {
          setIsDatadogReady(true);
        }, dataDogReplaceFallbackTime);

        datadogRum.onReady(() => {
          clearTimeout(fallbackTimeout);
          setIsDatadogReady(true);
        });
      } catch {
        setShouldSkipDatadog(true);
      }
    };

    initializeDatadog();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lagoonEnv]);
  return isDatadogReady || shouldSkipDatadog ? (
    <LDProvider>
      <App />
    </LDProvider>
  ) : null;
};
